import { PrimaryButton } from "@components/Buttons/PrimaryButton";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Chip, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { colors } from "../../../../../../ui/colors";
import { EmptyNumbersState } from "./components/EmptyNumberState";
import { LocalNumberBenefitCard } from "./components/LocalNumberBenefitCard";
import { NumberCard } from "./components/NumberCard";
import { PriceBreakdownCard } from "./components/PriceBreakdown";
import { PriceBreakdownSkeleton } from "./components/PriceBreakdownSkeleton";
import { SelectedPhoneNumberCard } from "./components/SelectedNumber";
import { SkeletonNumberCard } from "./components/SkeletonNumberCard";
import { useAvailableUsPhoneNumbers } from "./hooks/useAvailableUsPhoneNumbers";
import { usePricingUsPhoneNumber } from "./hooks/usePricingUsPhoneNumber";

const INITIAL_VISIBLE_COUNT = 4;
const LOAD_MORE_COUNT = 4;
const SEARCH_LIMIT = 30;

interface BuyPhoneNumberProps {
  selected?: PhoneNumber | null;
  onSelectedChange?: (number: PhoneNumber | null) => void;
  hideActions?: boolean;
}

export const BuyPhoneNumber = ({ selected: controlledSelected, onSelectedChange, hideActions }: BuyPhoneNumberProps) => {
  const numbersTopRef = useRef<HTMLDivElement | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [submittedAreaCode, setSubmittedAreaCode] = useState("");
  const [internalSelected, setInternalSelected] = useState<PhoneNumber | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

  const selected = controlledSelected !== undefined ? controlledSelected : internalSelected;

  const setSelectedNumber = (number: PhoneNumber | null) => {
    if (onSelectedChange) {
      onSelectedChange(number);
      return;
    }

    setInternalSelected(number);
  };

  const { data: availableUsPhoneNumbers = [], isFetching } = useAvailableUsPhoneNumbers({
    areaCode: submittedAreaCode,
    limit: SEARCH_LIMIT,
  });

  const visibleNumbers = availableUsPhoneNumbers.slice(0, visibleCount);

  const { data: pricing, isFetching: isPricingFetching } = usePricingUsPhoneNumber({
    phoneNumber: selected?.phoneNumber,
    enabled: !!selected?.phoneNumber,
  });

  const handleSearch = () => {
    if (searchInput && searchInput.length !== 3) return;

    setSelectedNumber(null);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    setSubmittedAreaCode(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSubmittedAreaCode("");
    setSelectedNumber(null);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 3);

    setSearchInput(value);

    if (value.length === 0) {
      setSelectedNumber(null);
      setVisibleCount(INITIAL_VISIBLE_COUNT);
      setSubmittedAreaCode("");
    }
  };

  const handleLoadMore = () => {
    setVisibleCount((current) => current + LOAD_MORE_COUNT);
  };

  const handleSelectNumber = (number: PhoneNumber, index: number) => {
    setSelectedNumber(number);
  };

  const hasMoreNumbers = visibleCount < availableUsPhoneNumbers.length;

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
      <Box sx={{ mx: "auto" }}>
        <Box sx={{ mb: 3.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 0.75,
            }}
          >
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 800,
                color: colors.gray[500],
                letterSpacing: "-0.03em",
              }}
            >
              Have a business phone number
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 14,
              color: colors.gray[400],
              fontWeight: 500,
            }}
          >
            Search by area code or find a memorable number for your business.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 280px" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Box>
            <Paper
              elevation={0}
              sx={{
                border: `1.5px solid ${colors.gray[100]}`,
                borderRadius: "16px",
                p: 2,
                mb: 2.5,
                boxShadow: `0 2px 12px ${colors.orange[400]}12`,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.25 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  inputMode="numeric"
                  placeholder="ex: 213"
                  value={searchInput}
                  onChange={handleInputChange}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  inputProps={{
                    maxLength: 3,
                    pattern: "[0-9]*",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: colors.gray[300], fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      bgcolor: colors.gray[50],
                      "& fieldset": {
                        border: `1.5px solid ${colors.gray[100]}`,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.orange[300],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.orange[500],
                      },
                    },
                    "& input": {
                      fontSize: 14,
                      fontWeight: 500,
                    },
                  }}
                />

                <PrimaryButton
                  variant="contained"
                  onClick={handleSearch}
                  disabled={!!searchInput && searchInput.length !== 3}
                  sx={{
                    width: 180,
                    height: "100%",
                    borderRadius: "10px",
                  }}
                >
                  {isFetching ? "Searching..." : "Search"}
                </PrimaryButton>
              </Box>
            </Paper>

            <Box
              ref={numbersTopRef}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
                scrollMarginTop: 24,
              }}
            >
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: colors.gray[500],
                }}
              >
                Available numbers
              </Typography>

              <Chip
                label={isFetching ? "Searching..." : `${availableUsPhoneNumbers.length} numbers available`}
                size="small"
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: colors.orange[50],
                  color: colors.orange[600],
                  border: `1px solid ${colors.orange[100]}`,
                  height: 24,
                }}
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {isFetching ? (
                Array.from({ length: INITIAL_VISIBLE_COUNT }).map((_, index) => <SkeletonNumberCard key={index} />)
              ) : visibleNumbers.length === 0 ? (
                <EmptyNumbersState onClearSearch={handleClearSearch} />
              ) : (
                visibleNumbers.map((number, index) => (
                  <NumberCard
                    key={number.phoneNumber}
                    number={number}
                    selected={selected?.phoneNumber === number.phoneNumber}
                    onSelect={() => handleSelectNumber(number, index)}
                  />
                ))
              )}
            </Box>

            {!isFetching && hasMoreNumbers && (
              <PrimaryButton
                variant="outlined"
                fullWidth
                onClick={handleLoadMore}
                sx={{
                  mt: 1,
                  height: "100%",
                  borderRadius: "10px",
                }}
              >
                Load more numbers
              </PrimaryButton>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <LocalNumberBenefitCard />

            {!hideActions &&
              selected &&
              (isPricingFetching ? <PriceBreakdownSkeleton /> : <PriceBreakdownCard pricingRows={pricing?.pricingRows ?? []} />)}

            {!hideActions && selected && <SelectedPhoneNumberCard selected={selected} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
